// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The logo's point cloud: a "half scan" of the X — points over both strokes
 * (A is drawn solid at rest and dissolves into its points on play; B is
 * always points) — plus, for every building part, one target position per
 * point, fitted inside the X's own box. DOM-free and seeded, so the same
 * layout always draws the same cloud.
 */

import { clamp, density, inStrokeA, inStrokeB, type WordLayout, type XGeometry } from './geometry';
import { PARTS } from './parts';
import { gauss, rng, type Rng } from './random';

/** Floats per point in `LogoModel.it`. */
export const STRIDE = 7;
/** Offsets into one point's record. */
export const P_X = 0;
export const P_Y = 1;
export const P_ALPHA = 2;
/** 1 = stroke A (solid at rest), 2 = stroke B (always points). */
export const P_KIND = 3;
/** A standard normal: how high this point arcs on its way. */
export const P_GAUSS = 4;
export const P_PHASE = 5;
/** The point's rank left to right: which target it takes, and when it leaves. */
export const P_ORDER = 6;

export const DEFAULT_SEED = 1234;

export interface LogoModel {
  /** Points, `STRIDE` floats each: x, y, alpha, kind, gauss, phase, order. */
  it: Float32Array;
  /** Number of points. */
  N: number;
  /** Per part (in `PARTS` order): N target positions, x0 y0 x1 y1 …, sorted left to right. */
  targets: Float32Array[];
  /** Dot radius in px. */
  rad: number;
}

/**
 * `n` points scattered over the strokes `test` accepts (it returns the kind,
 * 0 to reject), each nudged by scanner noise: mostly `jitter·d`, now and then
 * a stray at `1.4·d`. The nudge is capped at 2.5·d so no point leaves the
 * canvas's padding.
 */
function scanPoints(
  h: number,
  g: XGeometry,
  R: Rng,
  test: (u: number, v: number) => number,
  jitter: number,
): number[] {
  const { w, d, n } = density(h, g);
  const cap = 2.5 * d;
  const p: number[] = [];
  for (let i = 0; i < n; i++) {
    let u: number, v: number, s: number;
    do {
      u = R();
      v = R();
      s = test(u, v);
    } while (!s);
    const sg = (R() < 0.035 ? 1.4 : jitter) * d;
    const ox = clamp(gauss(R) * sg, -cap, cap);
    const oy = clamp(gauss(R) * sg, -cap, cap);
    p.push(u + ox / w, v + oy / h, 0.4 + 0.6 * Math.pow(R(), 0.6), s);
  }
  return p;
}

/** Pairs (x, y) reordered by x. */
function sortByU(a: number[]): Float32Array {
  const n = a.length / 2;
  const idx = Array.from({ length: n }, (_, i) => i).sort((i, j) => a[2 * i] - a[2 * j]);
  const s = new Float32Array(a.length);
  idx.forEach((i, k) => {
    s[2 * k] = a[2 * i];
    s[2 * k + 1] = a[2 * i + 1];
  });
  return s;
}

/** The cloud for `layout`'s X, and where each point goes for each part. */
export function generateModel(layout: WordLayout, g: XGeometry, seed = DEFAULT_SEED): LogoModel {
  const R = rng(seed);
  const XB = layout.X;
  const { d, r } = density(XB.h, g);
  // Stroke B leaves a gap where it would cross A, so the crossing reads as A on top.
  const gapU = (0.9 * d) / XB.w;
  const pts = scanPoints(XB.h, g, R, (u, v) => (inStrokeA(u, v, g) ? 1 : 0), 0.18).concat(
    scanPoints(XB.h, g, R, (u, v) => (inStrokeB(u, v, g) && !inStrokeA(u, v, g, gapU) ? 2 : 0), 0.18),
  );

  const N = pts.length / 4;
  const it = new Float32Array(N * STRIDE);
  for (let i = 0, k = 0; i < pts.length; i += 4, k += STRIDE) {
    it[k + P_X] = XB.x + pts[i] * XB.w;
    it[k + P_Y] = XB.y + pts[i + 1] * XB.h;
    it[k + P_ALPHA] = pts[i + 2];
    it[k + P_KIND] = pts[i + 3];
    it[k + P_GAUSS] = gauss(R);
    it[k + P_PHASE] = R() * Math.PI * 2;
    it[k + P_ORDER] = -1;
  }
  const order = Array.from({ length: N }, (_, i) => i).sort((a, b) => it[a * STRIDE] - it[b * STRIDE]);
  order.forEach((i, rank) => {
    it[i * STRIDE + P_ORDER] = rank;
  });

  // Each part is fitted inside the X's own box: never wider, never taller than the cap height.
  const cx = XB.x + XB.w / 2;
  const cy = XB.y + XB.h / 2;
  const targets = PARTS.map((part) => {
    const [x0, y0, x1, y1] = part.box;
    const k = Math.min(XB.w / (x1 - x0), XB.h / (y1 - y0));
    const mx = (x0 + x1) / 2;
    const my = (y0 + y1) / 2;
    const a: number[] = [];
    while (a.length < 2 * N) {
      const u = R();
      const v = R();
      const p = part.density(u, v);
      if (p && R() < p) a.push(u, v);
    }
    const s = sortByU(a);
    for (let i = 0; i < N; i++) {
      s[2 * i] = cx + (s[2 * i] - mx) * k;
      s[2 * i + 1] = cy + (s[2 * i + 1] - my) * k;
    }
    return s;
  });

  return { it, N, targets, rad: r };
}
